'use client';

import { useState } from 'react';
import ProcessStep from './ProcessStep';
import DocumentCard from './DocumentCard';
import CostEstimator from './CostEstimator';
import TimelineCard, { TimelineData } from './TimelineCard';
import ReadinessQuiz, { QuizQuestion } from './ReadinessQuiz';
import Icon from '@/components/ui/AppIcon';
import { StaggerContainer, StaggerItem, SlideUp } from '@/components/ui/motion';

interface ProcessStepData {
  stepNumber: number;
  title: string;
  description: string;
}

interface DocumentData {
  title: string;
  description: string;
  iconName: string;
  isRequired: boolean;
}

interface CostItem {
  category: string;
  description: string;
  minCost: number;
  maxCost: number;
}

interface CostEstimate {
  type: string;
  label: string;
  costs: CostItem[];
}


interface AdoptionInteractiveProps {
  processSteps: ProcessStepData[];
  documents: DocumentData[];
  costEstimates: CostEstimate[];
  timeline: TimelineData[];
  quizQuestions: QuizQuestion[];
}

export default function AdoptionInteractive({
  processSteps,
  documents,
  costEstimates = [], // Default to empty array
  timeline,
  quizQuestions,
}: AdoptionInteractiveProps) {
  const [currentStep, setCurrentStep] = useState(1);
  // Default to first available type or 'dog' if no estimates
  const [selectedPetType, setSelectedPetType] = useState<string>(
    (costEstimates && costEstimates.length > 0) ? costEstimates[0].type : 'dog'
  );
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <div className="space-y-12">
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-muted rounded-xl"></div>
          <div className="h-32 bg-muted rounded-xl"></div>
          <div className="h-32 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  const currentCostEstimate = costEstimates.find(c => c.type === selectedPetType) || (costEstimates.length > 0 ? costEstimates[0] : null);

  return (
    <div className="space-y-16">
      {/* Progress Tracker */}
      <section>
        <SlideUp>
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-headline font-semibold text-3xl text-foreground">Your Adoption Journey</h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-body text-muted-foreground">Progress:</span>
              <span className="text-lg font-headline font-semibold text-primary">
                {currentStep}/{processSteps.length}
              </span>
            </div>
          </div>
          <div className="space-y-4">
            {processSteps.map((step) => (
              <ProcessStep
                key={step.stepNumber}
                stepNumber={step.stepNumber}
                title={step.title}
                description={step.description}
                isCompleted={step.stepNumber < currentStep}
                isActive={step.stepNumber === currentStep}
              />
            ))}
          </div>
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className="px-6 py-2.5 border-2 border-border text-foreground font-body font-medium rounded-lg hover:bg-muted transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous Step
            </button>
            <button
              onClick={() => setCurrentStep(Math.min(processSteps.length, currentStep + 1))}
              disabled={currentStep === processSteps.length}
              className="px-6 py-2.5 bg-primary text-white font-headline font-semibold rounded-lg hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next Step
            </button>
          </div>
        </SlideUp>
      </section>

      {/* Required Documents */}
      <section>
        <SlideUp delay={0.1}>
          <div className="flex items-center space-x-3 mb-8">
            <Icon name="DocumentTextIcon" size={32} variant="outline" className="text-primary" />
            <h2 className="font-headline font-semibold text-3xl text-foreground">Required Documentation</h2>
          </div>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {documents.map((doc, index) => (
              <StaggerItem key={index}>
                <DocumentCard
                  title={doc.title}
                  description={doc.description}
                  iconName={doc.iconName}
                  isRequired={doc.isRequired}
                />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </SlideUp>
      </section>

      {/* Cost Estimator */}
      <section>
        <SlideUp delay={0.2}>
          <div className="flex items-center space-x-3 mb-8">
            <Icon name="CurrencyDollarIcon" size={32} variant="outline" className="text-primary" />
            <h2 className="font-headline font-semibold text-3xl text-foreground">Cost Planning</h2>
          </div>
          <div className="flex flex-wrap items-center gap-4 mb-8">
            {costEstimates.map((estimate) => (
              <button
                key={estimate.type}
                onClick={() => setSelectedPetType(estimate.type)}
                className={`px-6 py-3 rounded-lg font-body font-medium transition-all duration-300 ${selectedPetType === estimate.type
                  ? 'bg-primary text-white shadow-soft'
                  : 'bg-card text-foreground border border-border hover:border-primary'
                  }`}
              >
                {estimate.label}
              </button>
            ))}
          </div>
          {currentCostEstimate && (
            <CostEstimator
              petType={currentCostEstimate.label}
              costs={currentCostEstimate.costs}
            />
          )}
        </SlideUp>
      </section>

      {/* Timeline */}
      <section>
        <SlideUp delay={0.3}>
          <div className="flex items-center space-x-3 mb-8">
            <Icon name="CalendarIcon" size={32} variant="outline" className="text-primary" />
            <h2 className="font-headline font-semibold text-3xl text-foreground">Adoption Timeline</h2>
          </div>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {timeline.map((phase, index) => (
              <StaggerItem key={index}>
                <TimelineCard
                  phase={phase.phase}
                  duration={phase.duration}
                  activities={phase.activities}
                  iconName={phase.iconName}
                />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </SlideUp>
      </section>

      {/* Readiness Quiz */}
      <section>
        <SlideUp delay={0.4}>
          <div className="flex items-center space-x-3 mb-8">
            <Icon name="AcademicCapIcon" size={32} variant="outline" className="text-primary" />
            <h2 className="font-headline font-semibold text-3xl text-foreground">Adoption Readiness Assessment</h2>
          </div>
          <ReadinessQuiz questions={quizQuestions} />
        </SlideUp>
      </section>


    </div>
  );
}